import { useState } from "react";
import { CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Section, useToolkit } from "./parts";
import { Outreach, Social } from "./OutreachSocial";

const RESULTS = [["prospects_identified", "Prospects identified"], ["prospects_contacted", "Prospects contacted"], ["responses", "Responses received"], ["interested", "Interested prospects"],
  ["quotes_sent", "Quotes or offers sent"], ["paying_customers", "Paying customers acquired"], ["revenue", "Revenue collected (Rs)"]];
const DECISIONS = [["continue", "Continue Testing"], ["improve", "Improve My Offer"], ["another", "Try Another Idea"]];

function PlanDay({ number, p, onSaved }) {
  const [notes, setNotes] = useState(p.notes || "");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async (completed) => {
    setBusy(true);
    try { onSaved(p.day, (await api.put(`/ideas/${number}/toolkit/plan/${p.day}`, { completed, notes })).data); toast.success(completed ? `Day ${p.day} completed` : "Saved"); } catch { toast.error("Not saved, try again"); } finally { setBusy(false); }
  };
  return (
    <li className={`rounded-2xl border ${p.completed ? "border-[#0BAF9A] bg-[#F2FBF8]" : "border-[#E1ECEF]"}`} data-testid={`toolkit-day-${p.day}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="w-full text-left px-4 py-3 min-h-[56px] flex items-center gap-3" data-testid={`toolkit-day-toggle-${p.day}`}>
        {p.completed ? <CheckCircle2 className="h-6 w-6 text-[#0BAF9A] shrink-0" /> : <Circle className="h-6 w-6 text-[#B9CBD2] shrink-0" />}
        <span><span className="block text-xs font-extrabold text-[#088F7E]">DAY {p.day}</span><span className="block text-sm font-semibold text-[#102B3F]">{p.purpose}</span></span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-[15px] text-[#102B3F] whitespace-pre-wrap" data-testid={`toolkit-day-action-${p.day}`}>{p.action}</p>
          {p.evidence_prompt && <p className="text-sm text-[#5C6B73]"><b>Result to save:</b> {p.evidence_prompt}</p>}
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={2000} placeholder="Notes or results (optional)" className="w-full rounded-xl border border-[#D5E3E8] p-3 text-[15px]" data-testid={`toolkit-day-notes-${p.day}`} />
          <div className="flex flex-wrap gap-2">
            {!p.completed ? <button disabled={busy} onClick={() => save(true)} className="min-h-[48px] px-5 rounded-xl bg-[#0BAF9A] text-white font-bold" data-testid={`toolkit-day-complete-${p.day}`}>Mark Day {p.day} complete</button>
              : <button disabled={busy} onClick={() => save(false)} className="min-h-[48px] px-5 rounded-xl border border-[#D5E3E8] font-bold" data-testid={`toolkit-day-reopen-${p.day}`}>Reopen this day</button>}
            <button disabled={busy} onClick={() => save(!!p.completed)} className="min-h-[48px] px-5 rounded-xl border border-[#D5E3E8] font-bold" data-testid={`toolkit-day-save-${p.day}`}>Save notes</button>
          </div>
        </div>
      )}
    </li>
  );
}

function Summary({ number, d }) {
  const [r, setR] = useState(d.results || {});
  const [dec, setDec] = useState(d.decision || null);
  const save = async () => { try { await api.put(`/ideas/${number}/toolkit/results`, { results: r, decision: dec }); toast.success("Results saved"); } catch { toast.error("Not saved, try again"); } };
  return (
    <div className="rounded-2xl bg-[#102B3F] text-white p-4 space-y-3" data-testid="toolkit-results">
      <div className="font-display font-bold">Your 7-day results</div>
      <p className="text-xs text-white/70">Enter what really happened. These are your actual results, not projections.</p>
      <div className="grid grid-cols-2 gap-2">{RESULTS.map(([k, l]) => <label key={k} className="text-xs text-white/80">{l}<input type="number" min="0" inputMode="numeric" value={r[k] ?? ""} onChange={(e) => setR({ ...r, [k]: e.target.value === "" ? null : Number(e.target.value) })} className="mt-1 w-full h-11 rounded-xl px-3 text-[15px] text-[#102B3F]" data-testid={`toolkit-result-${k}`} /></label>)}</div>
      <div className="grid gap-2">{DECISIONS.map(([k, l]) => <button key={k} onClick={() => setDec(k)} aria-pressed={dec === k} className={`min-h-[48px] rounded-xl font-bold ${dec === k ? "bg-[#0BAF9A]" : "bg-white/10"}`} data-testid={`toolkit-decision-${k}`}>{l}</button>)}</div>
      <button onClick={save} className="w-full min-h-[48px] rounded-xl bg-white text-[#102B3F] font-bold" data-testid="toolkit-results-save">Save my results</button>
    </div>
  );
}

function Plan({ number, d, setD, open, onToggle }) {
  const done = d.plan.filter((p) => p.completed).length;
  const pct = d.plan.length ? Math.round((100 * done) / d.plan.length) : 0;
  const onSaved = (day, rec) => setD({ ...d, plan: d.plan.map((p) => (p.day === day ? { ...p, ...rec } : p)) });
  const badge = <span className="block text-xs font-bold text-[#088F7E] mt-1" data-testid="toolkit-plan-progress">{done} of {d.plan.length} days completed — {pct}%</span>;
  return (
    <Section id="plan" title="Your 7-Day First Client Plan" subtitle="Complete one practical action every day to start finding customers." open={open} onToggle={onToggle} badge={badge}>
      <div className="h-2 rounded-full bg-[#E1ECEF] overflow-hidden"><div className="h-full bg-[#0BAF9A]" style={{ width: `${pct}%` }} /></div>
      <ol className="space-y-2">{d.plan.map((p) => <PlanDay key={p.day} number={number} p={p} onSaved={onSaved} />)}</ol>
      {done === d.plan.length && d.plan.length > 0 && <Summary number={number} d={d} />}
      <p className="text-xs text-[#7D8C96]">A guided action plan — it does not guarantee customers.</p>
    </Section>
  );
}

export function FirstCustomerToolkit({ number }) {
  const [d, setD, err] = useToolkit(number);
  const [open, setOpen] = useState(null);
  if (err === 403 || err) return null;
  if (!d) return <div className="h-24 rounded-3xl bg-white border border-[#E1ECEF] animate-pulse" />;
  const tog = (k) => () => setOpen(open === k ? null : k);
  return (
    <div className="space-y-3" data-testid="first-customer-toolkit">
      <h2 className="font-display font-extrabold text-xl text-[#102B3F]">First Customer Toolkit</h2>
      <Outreach number={number} d={d} open={open === "outreach"} onToggle={tog("outreach")} />
      <Plan number={number} d={d} setD={setD} open={open === "plan"} onToggle={tog("plan")} />
      <Social number={number} d={d} open={open === "social"} onToggle={tog("social")} />
    </div>
  );
}
